import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { JdcManagerService } from './jdc-manager.service';
import { AppService } from '../../../app.service';
import { GlobalService } from '../../../global/services/global.service';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';

import { safeJsonInput } from '../../../shared/_utils/safe-json-input';
@Component({
    selector: 'gtw-jdc-manager',
    templateUrl: './jdc-manager.component.html',
    styleUrls: ['./jdc-manager.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class JdcManagerComponent implements OnInit, OnDestroy {
  jdcInfo: any = {
    loading: false,
    resultLoading: false,
    channelLoading: false,
    selectedChannel: null,
    taxYear: { taxYears: [] },
    scenario: { scenarios: [] },
    task: { tasks: [] },
    activity: { activities: [] },
    taskActivity: { taskActivities: [] },
    showResult: false,
  };
  channelList: any[] = [];
  jcdCombiList: any[] = [];
  settingsLoaded = false;
  jdcSettingsLoading = false;
  showJcds = false;
  showChannels = false;
  taxyearList: any[] = [];
  taskList: any[] = [];
  selectedTask: any = {};
  resetSelectedTask: any = {};
  activitiesList: any[] = [];
  saveTaskLoader = false;
  isAppParentChanged = false;
  taskModel: any = null;

  showReorderModal = false;
  showCopyAccessModal = false;
  copyAccessTargetActivity: any = null;
  showActivityUsersModal = false;
  activityUsersActivityKey: any = null;
  showChannelModal = false;
  channelModalMode: 'new' | 'edit' = 'new';
  channelModalData: any = null;
  showActivityModal = false;
  activityModalMode: 'new' | 'edit' = 'new';
  activityModalData: any = null;
  showTaskModal = false;
  taskModalMode: 'new' | 'edit' = 'new';
  taskMoveActive = false;
  taskMoveSource: any = null;

  taxyear: any = { channel: null, tax_year: null, edit_level: null, visible: null, is_default: '0', is_locked: 'N', editModalLoading: false };
  newScenario: any = { tax_year: null, scenario: null, scenario_desc: '', calendar_year: null, calendar_month: null };

  editlevelList = {
    list: [
      { name: 'User', value: 1 }, { name: 'Editor', value: 2 },
      { name: 'Admin', value: 3 }, { name: 'Developer', value: 4 },
      { name: 'Super Admin', value: 5 },
    ],
  };
  visibleList = {
    list: [
      { name: 'User', value: 1 }, { name: 'Editor', value: 2 },
      { name: 'Admin', value: 3 }, { name: 'Developer', value: 4 },
      { name: 'Super Admin', value: 5 },
    ],
  };

  tab = 0;
  jcdTab = 1;
  searchInput = '';
  searchText = '';
  refreshTaskActivity = false;
  trees: any = { nodes: [] };

  collapsedNodes = new Map<number, boolean>();
  appTabHover = false;
  settingsTabHover = false;
  taxYearHover = false;
  scenarioHover = false;
  taskActivityHover = false;

  private destroy$ = new Subject<void>();

  constructor(
    private jdcManagerService: JdcManagerService,
    private appService: AppService,
    private globalServiceRef: GlobalService,
    private gtwSnackbarService: GtwSnackbarService,
    private cdr: ChangeDetectorRef,
  ) {}

  get clientKey(): number | string {
    const userSettings = this.appService.USER_SETTINGS;
    return userSettings?.user?.client?.client_key
      ?? userSettings?.user?.client_key
      ?? sessionStorage.getItem('client_key')
      ?? '';
  }

  get client(): any {
    const user = this.appService.USER_SETTINGS?.user;
    if (user?.client?.client_key != null) return user.client;
    if (user?.client_key != null) {
      return { client_key: user.client_key, client_name: user.client_name || '' };
    }
    return {};
  }

  get globalParams(): any {
    return this.globalServiceRef?.global?.globalParams || {};
  }

  ngOnInit(): void {
    this.initData();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initData(): void {
    const userSettings = this.appService.USER_SETTINGS;
    if (!userSettings?.user) {
      return;
    }
    const ck = userSettings.user.client?.client_key ?? userSettings.user.client_key;
    if (ck == null) {
      return;
    }
    this.jdcInfo.loading = true;
    this.jdcInfo.channelLoading = true;
    this.cdr.detectChanges();

    this.jdcManagerService.getInitInfo({ client: { value: ck } })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.channelList = res?.jcdManage?.channelList
            ?? res?.data?.jcdManage?.channelList
            ?? [];
          this.jdcInfo.channelLoading = false;
          this.jdcInfo.loading = false;
          if (this.channelList.length > 0 && !this.jdcInfo.selectedChannel) {
            this.jdcInfo.selectedChannel = this.channelList.find((c: any) => c.channel_name === 'Tax Return') ?? this.channelList[0];
          }
          this.cdr.detectChanges();
        },
        error: (err) => {
          console.error('[JdcManager] getInitInfo error:', err);
          this.jdcInfo.channelLoading = false;
          this.jdcInfo.loading = false;
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Extracts a human-readable message from an HttpErrorResponse: a raw string
   * body, a `{ errorMessage | error | message }` object, or the error itself.
   * Used to surface backend access errors (e.g. "you do not have access to
   * client resources") returned with an HTTP error status code.
   */
  private extractApiErrorMessage(err: any, fallback: string): string {
    const body = err?.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (body && typeof body === 'object') {
      const fromBody = body.errorMessage || body.error || body.message;
      if (fromBody) {
        return fromBody;
      }
    }
    return err?.errorMessage || err?.message || fallback;
  }

  getResultForJDC(): void {
    const c = this.client;
    const ch = this.jdcInfo.selectedChannel;
    if (c?.client_key == null || !ch) return;
    const filterParams = {
      client: { name: c.client_name, value: c.client_key },
      jurisdiction: { name: 'US', value: 1 },
      domain: { name: 'Direct', value: 9 },
      channel: { name: ch.channel_name, value: ch.channel_key },
    };
    this.jdcInfo.resultLoading = true;
    this.refreshTaskActivity = true;
    this.cdr.detectChanges();
    this.jdcManagerService.getJCDKey(filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (keyRes) => {
          const raw = keyRes?.jcdKey ?? keyRes?.data?.jcdKey;
          const jcdKey = (raw != null) ? Number(raw) : null;
          this.jdcInfo.jcdKey = jcdKey;
          if (jcdKey != null && jcdKey !== 0 && !isNaN(jcdKey)) {
            this.loadJcdData(filterParams);
          } else {
            this.gtwSnackbarService.openSnackBar('Please select valid JCD combination.', 'gtw-snackbar--red');
            this.stopLoading(true);
          }
        },
        error: () => this.stopLoading(true),
      });
  }

  // Appkit tooltips escape their content, so the list is plain text; the
  // gtw-tooltip-multiline style class preserves the line breaks.
  buildTooltipList(items: string[], emptyText: string): string {
    if (!items?.length) return emptyText;
    return items.map(item => `\u2022 ${item}`).join('\n');
  }

  formatDate(value: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getFullYear()}`;
  }

  private stopLoading(showResult: boolean = false): void {
    this.jdcInfo.resultLoading = false;
    this.refreshTaskActivity = false;
    if (showResult) this.jdcInfo.showResult = true;
    this.cdr.detectChanges();
  }

  private loadJcdData(filterParams: any): void {
    this.jdcManagerService.loadResultForJDC(filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          this.jdcInfo.taxYear.taxYears = (resp?.tax_year ?? resp?.taxYearList ?? []).map((ty: any) => {
            const resolved = {
              ...ty,
              edit_level: ty.edit_level != null ? Number(ty.edit_level) : ty.edit_level,
              visible: ty.visible != null ? Number(ty.visible) : ty.visible,
            };
            const ck = ty.channel_key ?? ty.channel?.channel_key;
            const cn = ty.channel_name ?? ty.channel?.channel_name;
            resolved.channel = this.channelList.find((c: any) =>
              (ck != null && c.channel_key == ck) || (cn != null && c.channel_name === cn)
            ) ?? this.jdcInfo.selectedChannel ?? ty.channel ?? null;
            return resolved;
          });
          this.jdcInfo.scenario.scenarios = (resp?.scenario ?? resp?.scenarioList ?? []).map((s: any) => ({
            ...s,
            tax_year: s.tax_year != null ? Number(s.tax_year) : s.tax_year,
          }));
          const rawTaxYears = resp?.taxyearList ?? this.jdcInfo.taxYear.taxYears ?? [];
          this.taxyearList = rawTaxYears.map((t: any) => ({
            ...t,
            TAX_YEAR: Number(t.TAX_YEAR ?? t.tax_year ?? t.jcd_tax_year_key),
          }));
          this.loadTaxYearListForScenarios(filterParams);
          const taskActivityData = resp?.task_activity ?? [];
          if (Array.isArray(taskActivityData) && taskActivityData.length > 0) {
            this.trees.nodes = this.normalizeTaskActivityTree(taskActivityData);
            this.defaultSelection(this.trees.nodes, true);
            this.jdcInfo.taskActivity.taskActivities = taskActivityData;
          }
          this.jdcManagerService.getTaskForJCD(filterParams)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (taskRes) => {
                const list = taskRes?.taskList ?? taskRes?.data?.taskList ?? (Array.isArray(taskRes) ? taskRes : []);
                this.taskList = this.flattenTaskList(list);
                this.stopLoading(true);
              },
              error: () => this.stopLoading(true),
            });
        },
        error: () => this.stopLoading(true),
      });
  }

  private loadTaxYearListForScenarios(filterParams: any): void {
    this.jdcManagerService.getTaxYearForJCD(filterParams)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const taxYears = res?.jsonObject ?? res?.data?.jsonObject ?? [];
          if (Array.isArray(taxYears) && taxYears.length > 0) {
            this.taxyearList = taxYears.map((t: any) => ({
              ...t,
              TAX_YEAR: Number(t.TAX_YEAR ?? t.tax_year ?? t.jcd_tax_year_key),
            }));
            this.cdr.detectChanges();
          }
        },
        error: () => {}
      });
  }

  // --- Tree helpers ---

  private normalizeTaskActivityTree(taskActivity: any[]): any[] {
    return (taskActivity || []).map((task: any) => this.normalizeTaskNode({ ...task }));
  }

  private normalizeTaskNode(node: any): any {
    node.children = Array.isArray(node.children) ? node.children : [];
    node.sub_task = Array.isArray(node.sub_task) ? node.sub_task : [];
    node.task_name = node.task_name ?? node.name ?? '';
    node.client = this.client;
    node.channel = this.jdcInfo.selectedChannel;
    node.task_status = node.task_status || 'A';
    node.parentTask = node.parent_task_key || '';

    node.children = node.children
      .filter((a: any) => a && Number(a.activity_key || 0) !== 0)
      .map((activity: any) => {
        const a = { ...activity };
        a.roles = typeof a.roles === 'string' ? a.roles.split(',') : (a.roles || []);
        a.taxtypeList = typeof a.taxtypeList === 'string' ? a.taxtypeList.split(',') : (a.taxtypeList || []);
        a.screens = typeof a.screens === 'string' ? a.screens.split(',') : (a.screens || []);
        a.client_key = this.clientKey;
        return a;
      });

    node.sub_task = node.sub_task.map((child: any) => this.normalizeTaskNode({ ...child }));
    return node;
  }

  private flattenTaskList(tree: any[]): any[] {
    const flat: any[] = [];
    const recurse = (nodes: any[], level: number) => {
      (nodes || []).forEach((n: any) => {
        flat.push({ task_name: n.task_name, task_key: n.task_key, level, indent: '\u00A0\u00A0\u00A0'.repeat(level) });
        recurse(n.sub_task || [], level + 1);
      });
    };
    recurse(tree || [], 0);
    return flat;
  }

  toggleNode(taskKey: number): void {
    this.collapsedNodes.set(taskKey, !this.isCollapsed(taskKey));
    this.cdr.detectChanges();
  }

  isCollapsed(taskKey: number): boolean {
    return this.collapsedNodes.get(taskKey) ?? false;
  }

  matchesSearch(node: any): boolean {
    const search = (this.searchText || '').toLowerCase().trim();
    if (!search) return true;
    if ((node.task_name || '').toLowerCase().includes(search)) return true;
    if (Array.isArray(node.sub_task)) {
      return node.sub_task.some((child: any) => this.matchesSearch(child));
    }
    return false;
  }

  // --- Selection ---

  defaultSelection(data: any[], onLoad: boolean): void {
    if (!Array.isArray(data) || data.length === 0) {
      this.taskModel = null;
      this.selectedTask = {};
      this.activitiesList = [];
      return;
    }
    if (onLoad || !this.taskModel) {
      this.taskSelect(data[0], 'task');
      return;
    }
    this.taskModelObj(data, this.taskModel.task_key);
  }

  taskModelObj(data: any[], taskKey: number): boolean {
    for (let i = 0; i < data.length; i++) {
      const item = data[i];
      if (item.task_key === taskKey) {
        this.taskSelect(item, 'task');
        return true;
      }
      if (Array.isArray(item.sub_task) && this.taskModelObj(item.sub_task, taskKey)) {
        return true;
      }
    }
    return false;
  }

  taskSelect(task: any, _type: string): void {
    this.taskModel = task;
    this.selectedTask = {
      ...task,
      channel: task.channel || this.jdcInfo.selectedChannel,
      parentTask: task.parent_task_key || ''
    };
    this.resetSelectedTask = safeJsonInput<any>(JSON.stringify(this.selectedTask || {}), null, 'jdc-manager.parse1');
    this.activitiesList = Array.isArray(task.children) ? task.children : [];
    this.isAppParentChanged = false;
    this.cdr.detectChanges();
  }

  onTaskChanged(): void {
    this.isAppParentChanged = true;
  }

  // --- Internal helpers ---

  // listAllActivities and buildTaskSavePayload moved to child modal components

  // toMmDdYyyy and toDateInputValue moved to JdcSaveActivityModalComponent

  // --- Tax Years ---

  compareChannel(a: any, b: any): boolean {
    return a?.channel_key != null && b?.channel_key != null && a.channel_key === b.channel_key;
  }

  private resetNewTaxYear(): void {
    this.taxyear = {
      channel: null,
      tax_year: null,
      edit_level: null,
      visible: null,
      is_default: '0',
      is_locked: 'N',
      editModalLoading: false,
    };
  }

  onNewTaxYearChange(value: string | number | null): void {
    const digits = String(value ?? '').replace(/\D/g, '').slice(0, 4);
    this.taxyear.tax_year = digits.length ? Number(digits) : null;
    this.cdr.detectChanges();
  }

  toggleNewTaxYearDefault(): void {
    this.taxyear.is_default = this.taxyear.is_default === '1' ? '0' : '1';
    this.cdr.detectChanges();
  }

  toggleNewTaxYearLock(): void {
    this.taxyear.is_locked = this.taxyear.is_locked === 'Y' ? 'N' : 'Y';
    this.cdr.detectChanges();
  }

  addNewYear(): void {
    this.taxyear.editModalLoading = true;
    const taxYearDigits = String(this.taxyear.tax_year ?? '');
    if (!this.taxyear.channel || taxYearDigits.length !== 4 || !this.taxyear.edit_level || !this.taxyear.visible) {
      this.taxyear.editModalLoading = false;
      this.gtwSnackbarService.openSnackBar('Required fields are missing.', 'gtw-snackbar--red');
      return;
    }
    const payload = [{
      JCD_KEY: this.jdcInfo.jcdKey,
      TAX_YEAR: this.taxyear.tax_year,
      EDIT_LEVEL: this.taxyear.edit_level,
      VISIBLE: this.taxyear.visible,
      IS_DEFAULT: this.taxyear.is_default || '0',
      IS_LOCKED: this.taxyear.is_locked || 'N'
    }];
    this.jdcManagerService.saveJsonAction('8vygsp', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.taxyear.editModalLoading = false;
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Tax year saved successfully.', 'gtw-snackbar--green');
            this.resetNewTaxYear();
            this.getResultForJDC();
          } else {
            this.gtwSnackbarService.openSnackBar('Problem saving tax year.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.taxyear.editModalLoading = false;
          this.gtwSnackbarService.openSnackBar('Problem saving tax year.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      });
  }

  editTaxYear(taxyear: any): void {
    taxyear.editModalLoading = true;
    const payload = [{
      JCD_KEY: this.jdcInfo.jcdKey,
      JCD_TAX_YEAR_KEY: taxyear.jcd_tax_year_key,
      TAX_YEAR: taxyear.tax_year,
      EDIT_LEVEL: Number(taxyear.edit_level),
      VISIBLE: Number(taxyear.visible),
      IS_DEFAULT: String(taxyear.is_default),
      IS_LOCKED: taxyear.is_locked
    }];
    this.jdcManagerService.saveJsonAction('56p5yf', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          taxyear.editModalLoading = false;
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Tax year edited successfully.', 'gtw-snackbar--green');
            this.getResultForJDC();
          } else {
            // callSuccess=0: surface the API message and reload to roll back any
            // optimistic toggle (default / lock) to the true server state.
            this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Problem editing tax year.', 'gtw-snackbar--red');
            this.getResultForJDC();
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          taxyear.editModalLoading = false;
          this.gtwSnackbarService.openSnackBar(this.extractApiErrorMessage(err, 'Problem editing tax year.'), 'gtw-snackbar--red');
          this.getResultForJDC();
          this.cdr.detectChanges();
        }
      });
  }

  lockUnlockTaxYear(taxyear: any): void {
    taxyear.is_locked = taxyear.is_locked === 'Y' ? 'N' : 'Y';
    this.editTaxYear(taxyear);
  }

  defaultTaxYear(taxyear: any): void {
    taxyear.is_default = taxyear.is_default === '1' ? '0' : '1';
    this.editTaxYear(taxyear);
  }

  removeTaxYear(jcdTaxYearKey: number): void {
    this.jdcManagerService.saveJsonAction('t9j7xx', [{ JCD_TAX_YEAR_KEY: jcdTaxYearKey }])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Tax year removed successfully.', 'gtw-snackbar--green');
            this.getResultForJDC();
          } else {
            this.gtwSnackbarService.openSnackBar('Tax year not removed.', 'gtw-snackbar--red');
          }
        },
        error: () => this.gtwSnackbarService.openSnackBar('Tax year not removed.', 'gtw-snackbar--red')
      });
  }

  // --- Scenarios ---

  addNewScenario(): void {
    if (!this.newScenario.tax_year || !this.newScenario.scenario || !this.newScenario.scenario_desc) {
      this.gtwSnackbarService.openSnackBar('Required fields are missing.', 'gtw-snackbar--red');
      return;
    }
    const payload = [{
      TAX_YEAR: this.newScenario.tax_year, SCENARIO: this.newScenario.scenario,
      SCENARIO_DESC: this.newScenario.scenario_desc, JCD_KEY: this.jdcInfo.jcdKey,
      CALENDAR_YEAR: this.newScenario.calendar_year || '', CALENDAR_MONTH: this.newScenario.calendar_month || '',
      REPORTING_Y_N: 'N', FILING_Y_N: 'N', DEFAULT_SCENARIO: 'N', IS_VISIBLE: 'N', IS_LOCKED: 'N'
    }];
    this.jdcManagerService.saveJsonAction('most83', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Scenario saved successfully.', 'gtw-snackbar--green');
            this.getResultForJDC();
          } else {
            this.gtwSnackbarService.openSnackBar('Problem saving scenario.', 'gtw-snackbar--red');
          }
        },
        error: () => this.gtwSnackbarService.openSnackBar('Problem saving scenario.', 'gtw-snackbar--red')
      });
  }

  editScenario(scenario: any): void {
    scenario.editModalLoading = true;
    const payload = [{
      TAX_YEAR: scenario.tax_year, SCENARIO: scenario.scenario, SCENARIO_DESC: scenario.scenario_desc,
      JCD_KEY: this.jdcInfo.jcdKey, CALENDAR_YEAR: scenario.calendar_year || '',
      CALENDAR_MONTH: scenario.calendar_month || '', REPORTING_Y_N: scenario.reporting_y_n || 'N',
      FILING_Y_N: scenario.filing_y_n || 'N', DEFAULT_SCENARIO: scenario.default_scenario || 'N',
      IS_VISIBLE: scenario.is_visible || 'N', IS_LOCKED: scenario.is_locked || 'N',
      OLD_SCENARIO_KEY: scenario.scenario, OLD_TAX_YEAR: scenario.tax_year
    }];
    this.jdcManagerService.saveJsonAction('9khx7b', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          scenario.editModalLoading = false;
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Scenario edited successfully.', 'gtw-snackbar--green');
            this.getResultForJDC();
          } else {
            // callSuccess=0: surface the API message and reload to roll back any
            // optimistic scenario toggle to the true server state.
            this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Problem editing scenario.', 'gtw-snackbar--red');
            this.getResultForJDC();
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          scenario.editModalLoading = false;
          this.gtwSnackbarService.openSnackBar(this.extractApiErrorMessage(err, 'Problem editing scenario.'), 'gtw-snackbar--red');
          this.getResultForJDC();
          this.cdr.detectChanges();
        }
      });
  }

  removeScenario(scenario: number): void {
    this.jdcManagerService.saveJsonAction('vobhrd', [{ SCENARIO: scenario }])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Scenario removed successfully.', 'gtw-snackbar--green');
            this.getResultForJDC();
          } else {
            this.gtwSnackbarService.openSnackBar('Scenario not removed.', 'gtw-snackbar--red');
          }
        },
        error: () => this.gtwSnackbarService.openSnackBar('Scenario not removed.', 'gtw-snackbar--red')
      });
  }

  // --- Tasks ---

  saveTaskDetails(mode: 'edit' | 'new' = 'edit'): void {
    if (!this.selectedTask?.channel || !this.selectedTask?.task_name || !this.selectedTask?.task_desc) {
      this.gtwSnackbarService.openSnackBar('Required task fields are missing.', 'gtw-snackbar--red');
      return;
    }
    this.saveTaskLoader = true;
    const payload = [{
      TASK_PRIORITY: this.selectedTask.task_priority || 1,
      TASK_KEY: mode === 'edit' ? this.selectedTask.task_key : undefined,
      TASK_NAME: this.selectedTask.task_name,
      TASK_DESC: this.selectedTask.task_desc,
      TASK_STATUS: this.selectedTask.task_status || 'A',
      JCD_KEY: this.jdcInfo.jcdKey,
      PARENT_TASK_KEY: this.selectedTask.parentTask || '',
      CLIENT_KEY: this.clientKey
    }];
    const actionCode = mode === 'edit' ? '4dk04a' : '6aoylm';
    this.jdcManagerService.saveJsonAction(actionCode, payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.saveTaskLoader = false;
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar(`Task ${mode === 'edit' ? 'saved' : 'created'} successfully.`, 'gtw-snackbar--green');
            this.isAppParentChanged = false;
            this.getResultForJDC();
          } else {
            this.gtwSnackbarService.openSnackBar('Problem saving task.', 'gtw-snackbar--red');
          }
          this.cdr.detectChanges();
        },
        error: () => {
          this.saveTaskLoader = false;
          this.gtwSnackbarService.openSnackBar('Problem saving task.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      });
  }

  addNewTask(): void {
    this.taskModalMode = 'new';
    this.showTaskModal = true;
    this.cdr.detectChanges();
  }

  // --- Task tree move (Click to move) ---

  startTaskMove(node: any): void {
    this.taskMoveActive = true;
    this.taskMoveSource = node;
    this.clearTaskMoveFlags(this.trees.nodes);
    node.highlightNode = true;
    node.isMoveSelected = true;
    node.nodeToMove = true;
    this.markMoveDescendants(node.sub_task || [], true);
    this.markMoveParent(node, this.trees.nodes);
    this.cdr.detectChanges();
  }

  cancelTaskMove(): void {
    this.taskMoveActive = false;
    this.taskMoveSource = null;
    this.clearTaskMoveFlags(this.trees.nodes);
    this.cdr.detectChanges();
  }

  onTaskRowMouseEnter(node: any): void {
    if (!this.taskMoveActive) return;
    if (node.nodeToMove) {
      node.dropIcon = false;
      return;
    }
    if (this.canDropTaskOn(node)) {
      node.dropIcon = true;
      this.cdr.detectChanges();
    }
  }

  onTaskRowMouseLeave(node: any): void {
    node.dropIcon = false;
    if (this.taskMoveActive) {
      this.cdr.detectChanges();
    }
  }

  canDropTaskOn(destNode: any): boolean {
    if (!this.taskMoveActive || !this.taskMoveSource || !destNode) return false;
    if (destNode.task_key === this.taskMoveSource.task_key) return false;
    if (this.isTaskDescendant(destNode, this.taskMoveSource.task_key)) return false;
    return true;
  }

  confirmDropTask(destNode: any): void {
    if (!this.canDropTaskOn(destNode)) return;
    this.askConfirmation(
      'Move Task',
      `Are you sure you want to move "${this.taskMoveSource?.task_name}" under "${destNode.task_name}"?`,
      () => this.dropTaskOn(destNode)
    );
  }

  private dropTaskOn(destNode: any): void {
    const sourceKey = this.taskMoveSource?.task_key;
    if (sourceKey == null) return;
    const moved = this.extractTaskFromTree(this.trees.nodes, sourceKey);
    if (!moved) {
      this.gtwSnackbarService.openSnackBar('Unable to move task.', 'gtw-snackbar--red');
      this.cancelTaskMove();
      return;
    }
    moved.parent_task_key = destNode.task_key;
    moved.parentTask = destNode.task_key;
    if (!destNode.sub_task) destNode.sub_task = [];
    destNode.sub_task.push(moved);
    const payload = this.jdcManagerService.buildTaskTreePayload(
      this.trees.nodes,
      this.jdcInfo.jcdKey,
      this.clientKey
    );
    this.refreshTaskActivity = true;
    this.cancelTaskMove();
    this.cdr.detectChanges();
    this.jdcManagerService.saveJsonAction('4dk04a', payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.refreshTaskActivity = false;
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Task moved successfully.', 'gtw-snackbar--green');
            this.getResultForJDC();
          } else {
            this.gtwSnackbarService.openSnackBar('Problem moving task.', 'gtw-snackbar--red');
            this.cdr.detectChanges();
          }
        },
        error: () => {
          this.refreshTaskActivity = false;
          this.gtwSnackbarService.openSnackBar('Problem moving task.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      });
  }

  private clearTaskMoveFlags(nodes: any[]): void {
    (nodes || []).forEach((n) => {
      n.highlightNode = false;
      n.nodeToMove = false;
      n.dropIcon = false;
      n.isMoveSelected = false;
      if (n.sub_task?.length) this.clearTaskMoveFlags(n.sub_task);
    });
  }

  private markMoveDescendants(nodes: any[], flag: boolean): void {
    (nodes || []).forEach((n) => {
      n.nodeToMove = flag;
      n.highlightNode = flag;
      if (n.sub_task?.length) this.markMoveDescendants(n.sub_task, flag);
    });
  }

  private markMoveParent(srcNode: any, nodes: any[]): boolean {
    for (const n of nodes || []) {
      if (srcNode.parent_task_key != null && srcNode.parent_task_key === n.task_key) {
        n.nodeToMove = true;
        return true;
      }
      if (this.markMoveParent(srcNode, n.sub_task || [])) {
        return true;
      }
    }
    return false;
  }

  private isTaskDescendant(node: any, ancestorTaskKey: number): boolean {
    if (!node?.sub_task?.length) return false;
    return node.sub_task.some((child: any) =>
      child.task_key === ancestorTaskKey || this.isTaskDescendant(child, ancestorTaskKey)
    );
  }

  private extractTaskFromTree(nodes: any[], taskKey: number): any | null {
    for (let i = 0; i < (nodes || []).length; i++) {
      if (nodes[i].task_key === taskKey) {
        return nodes.splice(i, 1)[0];
      }
      const removed = this.extractTaskFromTree(nodes[i].sub_task || [], taskKey);
      if (removed) return removed;
    }
    return null;
  }

  removeTask(taskKey: number): void {
    this.jdcManagerService.removeTask(taskKey, this.clientKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Task removed successfully.', 'gtw-snackbar--green');
            this.getResultForJDC();
          } else {
            this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Task not removed.', 'gtw-snackbar--red');
          }
        },
        error: () => this.gtwSnackbarService.openSnackBar('Task not removed.', 'gtw-snackbar--red')
      });
  }

  resetTaskDetails(): void {
    this.selectedTask = safeJsonInput<any>(JSON.stringify(this.resetSelectedTask || {}), null, 'jdc-manager.parse2');
    this.isAppParentChanged = false;
    this.cdr.detectChanges();
  }

  // --- Activities ---

  removeActivity(activityKey: number, jcdKey: number): void {
    this.jdcManagerService.deleteActivity(activityKey, jcdKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if ((res?.result?.value ?? res?.data?.result?.value) === '1' || res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Activity removed successfully.', 'gtw-snackbar--green');
            this.getResultForJDC();
          } else {
            this.gtwSnackbarService.openSnackBar('Activity not removed.', 'gtw-snackbar--red');
          }
        },
        error: () => this.gtwSnackbarService.openSnackBar('Activity not removed.', 'gtw-snackbar--red')
      });
  }

  addNewActivity(): void {
    this.activityModalMode = 'new';
    this.activityModalData = null;
    this.showActivityModal = true;
    this.cdr.detectChanges();
  }

  editActivity(activity: any): void {
    this.activityModalMode = 'edit';
    this.activityModalData = activity;
    this.showActivityModal = true;
    this.cdr.detectChanges();
  }

  // --- Copy Access Modal ---

  copyAccessRoles(activity: any): void {
    this.copyAccessTargetActivity = activity;
    this.showCopyAccessModal = true;
    this.cdr.detectChanges();
  }

  // --- Activity Users Modal ---

  getActivityUsers(activity: any): void {
    this.activityUsersActivityKey = activity?.activity_key;
    this.showActivityUsersModal = true;
    this.cdr.detectChanges();
  }

  // --- Reorder Tasks Modal ---

  ReorderTasks(): void {
    if (!this.trees?.nodes?.length) {
      this.gtwSnackbarService.openSnackBar('No tasks available for reorder.', 'gtw-snackbar--yellow');
      return;
    }
    this.showReorderModal = true;
    this.cdr.detectChanges();
  }

  // --- Activity Reorder (Shuffle) ---

  dropActivities(event: CdkDragDrop<any[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    moveItemInArray(this.activitiesList, event.previousIndex, event.currentIndex);
    this.saveShuffledActivities(this.activitiesList, true);
  }

  private saveShuffledActivities(activities: any[], reordered: boolean): void {
    const activitiesForSaving = activities.map((act: any, i: number) => ({
      ACTIVITY_PRIORITY: i + 1,
      JCD_KEY: this.jdcInfo.jcdKey,
      ACTIVITY_KEY: act.activity_key,
      CLIENT_KEY: act.client_key ?? this.clientKey,
      TASK_KEY: act.task_key,
      ACTIVITY_NAME: act.activity_name
    }));
    this.jdcManagerService.shuffleActivity(JSON.stringify(activitiesForSaving))
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const value = res?.result?.value ?? res?.data?.result?.value;
          if (value === '1' || value === 1) {
            this.gtwSnackbarService.openSnackBar(
              reordered ? 'Activities reordered successfully.' : 'Activity moved successfully.',
              'gtw-snackbar--green'
            );
          } else {
            // Save rejected: surface the message and reload so the optimistic
            // drag-drop reorder is rolled back to the true server order.
            this.gtwSnackbarService.openSnackBar(res?.errorMessage || 'Problem reordering activities.', 'gtw-snackbar--red');
            this.getResultForJDC();
          }
          this.cdr.detectChanges();
        },
        error: (err: any) => {
          this.gtwSnackbarService.openSnackBar(this.extractApiErrorMessage(err, 'Problem reordering activities.'), 'gtw-snackbar--red');
          this.getResultForJDC();
          this.cdr.detectChanges();
        }
      });
  }

  // --- Assign Template Modal (moved to JdcSaveActivityModalComponent) ---

  // --- Application Settings Tab ---

  onJcdTabChange(tabId: number): void {
    this.jcdTab = tabId;
    if (this.jcdTab === 2 && !this.settingsLoaded) {
      this.loadResultForJDCSettings();
    }
    this.cdr.detectChanges();
  }

  toggleJcdComb(): void { this.showJcds = !this.showJcds; }
  toggleChannels(): void { this.showChannels = !this.showChannels; }

  loadResultForJDCSettings(): void {
    const ck = this.clientKey;
    if (ck == null || ck === '') return;
    this.jdcSettingsLoading = true;
    this.cdr.detectChanges();
    this.jdcManagerService.loadResultForJDCSettings(ck)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          try {
            this.jcdCombiList = safeJsonInput<any>(resp?.data4 || '[]', null, 'jdc-manager.parse3');
          } catch { this.jcdCombiList = []; }
          try {
            const allChannels = safeJsonInput<any>(resp?.data3 || '[]', null, 'jdc-manager.parse4');
            if (allChannels?.length) this.channelList = allChannels;
          } catch { /* keep existing channelList */ }
          this.settingsLoaded = true;
          this.jdcSettingsLoading = false;
          this.showJcds = true;
          this.showChannels = true;
          this.cdr.detectChanges();
        },
        error: () => {
          this.jdcSettingsLoading = false;
          this.gtwSnackbarService.openSnackBar('Problem loading application settings.', 'gtw-snackbar--red');
          this.cdr.detectChanges();
        }
      });
  }

  // --- Template Modal state moved to JdcSaveActivityModalComponent ---

  // --- Confirmation Modal ---
  showConfirmModal = false;
  confirmModalConfig: ConfirmationModalConfig = { title: '', message: '' };
  private confirmCallback: (() => void) | null = null;

  askConfirmation(title: string, message: string, callback: () => void): void {
    this.confirmModalConfig = {
      title,
      message,
      confirmButtonText: 'Confirm',
      cancelButtonText: 'Cancel',
      confirmButtonClass: 'btn-danger',
      cancelButtonClass: 'btn-default',
      showCancelButton: true,
      showConfirmButton: true,
    };
    this.confirmCallback = callback;
    this.showConfirmModal = true;
    this.cdr.detectChanges();
  }

  executeConfirm(): void {
    this.showConfirmModal = false;
    if (this.confirmCallback) { this.confirmCallback(); }
    this.confirmCallback = null;
    this.cdr.detectChanges();
  }

  cancelConfirm(): void {
    this.showConfirmModal = false;
    this.confirmCallback = null;
    this.cdr.detectChanges();
  }

  // --- New JCD KEY Modal ---
  showAddJcdModal = false;
  jcdModalMode: 'new' | 'edit' = 'new';
  jcdModalData: any = null;

  openAddJcdModal(): void {
    this.jcdModalMode = 'new';
    this.jcdModalData = null;
    this.showAddJcdModal = true;
    this.cdr.detectChanges();
  }

  editJCD(jcd: any): void {
    this.jcdModalMode = 'edit';
    this.jcdModalData = jcd;
    this.showAddJcdModal = true;
    this.cdr.detectChanges();
  }

  removeJCD(jcdInfo: any): void {
    this.jdcManagerService.deleteJcd(jcdInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if ((res?.result?.value ?? res?.data?.result?.value) === '1') {
            this.gtwSnackbarService.openSnackBar(`JCD ${jcdInfo?.jcd_key} removed successfully.`, 'gtw-snackbar--green');
            this.jcdCombiList = this.jcdCombiList.filter((j) => j.jcd_key !== jcdInfo?.jcd_key);
            this.cdr.detectChanges();
          } else {
            this.gtwSnackbarService.openSnackBar('JCD combination not removed.', 'gtw-snackbar--red');
          }
        },
        error: () => this.gtwSnackbarService.openSnackBar('JCD combination not removed.', 'gtw-snackbar--red')
      });
  }

  // --- Channel Modal ---

  addNewChannel(): void {
    this.channelModalMode = 'new';
    this.channelModalData = null;
    this.showChannelModal = true;
    this.cdr.detectChanges();
  }

  editChannel(channel: any): void {
    this.channelModalMode = 'edit';
    this.channelModalData = channel;
    this.showChannelModal = true;
    this.cdr.detectChanges();
  }

  removeChanel(channelKey: number): void {
    this.jdcManagerService.saveJsonAction('e3x0h6', [{ CHANNEL_KEY: channelKey }])
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.callSuccess === '1') {
            this.gtwSnackbarService.openSnackBar('Channel removed successfully.', 'gtw-snackbar--green');
            this.channelList = this.channelList.filter((c) => c.channel_key !== channelKey);
            this.cdr.detectChanges();
          } else {
            this.gtwSnackbarService.openSnackBar('Channel is in use and cannot be removed.', 'gtw-snackbar--red');
          }
        },
        error: () => this.gtwSnackbarService.openSnackBar('Channel is in use and cannot be removed.', 'gtw-snackbar--red')
      });
  }
}
